// src/routes/+layout.ts
import { browser } from "$app/environment";
import {
	createBrowserClient,
	createServerClient,
	isBrowser,
	parse,
} from "@supabase/ssr";
import {
	PUBLIC_SUPABASE_ANON_KEY,
	PUBLIC_SUPABASE_URL,
} from "$env/static/public";

import { QueryClient } from "@tanstack/svelte-query";
import { logger } from "$lib/logger.js";
import { APP_REDIRECTS } from "../redirects";
import { redirect } from "@sveltejs/kit";

export const load = async (context) => {
	const { fetch, data, depends, parent, url } = context;

	/**
	 * Declare a dependency so the layout can be invalidated, for example, on
	 * session refresh.
	 */
	depends("supabase:auth");

	const supabase = isBrowser()
		? createBrowserClient(PUBLIC_SUPABASE_URL, PUBLIC_SUPABASE_ANON_KEY, {
				global: {
					fetch,
				},
				cookies: {
					get(key) {
						const cookie = parse(document.cookie);
						return cookie[key];
					},
				},
			})
		: createServerClient(PUBLIC_SUPABASE_URL, PUBLIC_SUPABASE_ANON_KEY, {
				global: {
					fetch,
				},
				cookies: {
					get() {
						return JSON.stringify(data.session);
					},
				},
			});

	/**
	 * It's fine to use `getSession` here, because on the client, `getSession` is
	 * safe, and on the server, it reads `session` from the `LayoutData`, which
	 * safely checked the session using `safeGetSession`.
	 */
	const {
		data: { session },
	} = await supabase.auth.getSession();

	const {
		data: { user },
	} = await supabase.auth.getUser();

	const { accounts, deviceType, collapsed, layout, testEnv } = data;

	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				enabled: browser,
				staleTime: 5 * 60 * 1000,
			},
		},
	});

	const pathname = url.pathname;
    if (APP_REDIRECTS.hasOwnProperty(pathname)) {
        return redirect(301, (APP_REDIRECTS as any)[pathname]);
    }

	// The session can still be null in this file
	return {
		session,
		supabase,
		queryClient,
		user,
		accounts,
		deviceType,
		collapsed,
		layout,
		testEnv,
	};
};
